import { cssBundleHref } from "@remix-run/css-bundle";
import type { LinksFunction, MetaFunction } from "@remix-run/node";
import {
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useRouteError } from
"@remix-run/react";
import animationsStylesheet from "@ruter-ds-next/animations/index.css";
import buttonStylesheet from "@ruter-ds-next/button/index.css";
import inputStylesheet from "@ruter-ds-next/input/index.css";
import radioStylesheet from "@ruter-ds-next/radio/index.css";
import searchStylesheet from "@ruter-ds-next/searchfield/index.css";
import selectStylesheet from "@ruter-ds-next/select/index.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";

import highlightJsTemplate from "./atom-one-dark.css";
import {
  UnauthorizedErrorMessage,
  UnexpectedErrorMessage,
  ValidationErrorMessage } from
"./components/Error";
import { PageWrapper } from "./components/PageWrapper";
import stylesheet from "./tailwind.css";

dayjs.extend(utc);

export const links: LinksFunction = () => [
{ rel: "stylesheet", href: stylesheet },
{ rel: "stylesheet", href: highlightJsTemplate },
{ rel: "stylesheet", href: buttonStylesheet },
{ rel: "stylesheet", href: inputStylesheet },
{ rel: "stylesheet", href: searchStylesheet },
{ rel: "stylesheet", href: selectStylesheet },
{ rel: "stylesheet", href: radioStylesheet },
{ rel: "stylesheet", href: animationsStylesheet },
...(cssBundleHref ? [{ rel: "stylesheet", href: cssBundleHref }] : [])];


export const meta: MetaFunction = () => {
  return [
  { title: "Ruter VVS" },
  {
    name: "description",
    content: "A tool for verifying DPI rig on new vehicles"
  },
  {
    property: "og:title",
    content: "Ruter Vehicle Verification System"
  },
  {
    property: "og:type",
    content: "website"
  },
  {
    property: "og:image",
    content: "/ruter-vvs.webp"
  }];

};

export default function App() {
  const location = useLocation();
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <Outlet />
        {location.pathname.startsWith("/vvs") ? null : <ScrollRestoration />}
        <Scripts />
        <LiveReload />
      </body>
    </html>);

}

export function ErrorBoundary() {
  const error = useRouteError();

  let errorComponent =
  <UnexpectedErrorMessage>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {`${(error as any)?.message}\n\n${(error as any)?.stack ?? ""}`}
    </UnexpectedErrorMessage>;


  /**
   * Error caused by _us_ throwing a Response object
   * in our route handlers
   */
  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      errorComponent = <UnauthorizedErrorMessage />;
    } else {
      errorComponent =
      <ValidationErrorMessage>{error?.data}</ValidationErrorMessage>;

    }
  }

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Meta />
        <Links />
        <title>Oh-no 😓</title>
      </head>
      <body>
        <PageWrapper pageClassName="bg-red-100" className="text-center">
          {errorComponent}
          <Link to="/" className="mt-12 block">
            To frontpage
          </Link>
        </PageWrapper>
        <ScrollRestoration />
        <Scripts />
        <LiveReload port={8002} />
      </body>
    </html>);

}